import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCamera,
  FaCheck,
  FaEnvelope,
  FaExternalLinkAlt,
  FaIdBadge,
  FaImage,
  FaLink,
  FaPen,
  FaRegUserCircle,
  FaSave,
  FaShieldAlt,
  FaTimes,
  FaUser,
} from "react-icons/fa";

import useScrollToTop from "./../../hooks/useScollToTop";
import { updateUserProfile } from "./../../services/userServices";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import { useUser } from "./../Home/UserContext";
import { useNotification } from "./../../components/Notification/NotificationManager";

const DEFAULT_AVATAR = "/img/user_avatar.jpg";

const avatars = [
  "/img/avatar/avatarF001.webp",
  "/img/avatar/avatarF002.webp",
  "/img/avatar/avatarF003.webp",
  "/img/avatar/avatarF004.webp",
  "/img/avatar/avatarF005.webp",
  "/img/avatar/avatarF006.webp",
  "/img/avatar/avatarF007.webp",
  "/img/avatar/avatarF008.webp",
  "/img/avatar/avatarM001.webp",
  "/img/avatar/avatarM002.webp",
  "/img/avatar/avatarM003.webp",
  "/img/avatar/avatarM004.webp",
  "/img/avatar/avatarM005.webp",
  "/img/avatar/avatarM006.webp",
  "/img/avatar/avatarM007.webp",
  "/img/avatar/avatarM008.webp",
  "/img/avatar/avatarM009.webp",
];

const safeParseJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const isValidHttpUrl = (value = "") => {
  try {
    const url = new URL(String(value).trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const getUserId = (user) => user?.userId || user?.id || null;

const getUserPhoto = (user) =>
  user?.photoURL || user?.photoUrl || user?.photo_url || DEFAULT_AVATAR;

const normalizeUserData = (updatedUser, previousUser = {}) => ({
  ...previousUser,
  userId: updatedUser?.id || updatedUser?.userId || previousUser?.userId,
  id: updatedUser?.id || previousUser?.id,
  nom: updatedUser?.nom ?? previousUser?.nom ?? "",
  prenom: updatedUser?.prenom ?? previousUser?.prenom ?? "",
  email: updatedUser?.email ?? previousUser?.email ?? "",
  photoURL:
    updatedUser?.photoURL ||
    updatedUser?.photoUrl ||
    updatedUser?.photo_url ||
    previousUser?.photoURL ||
    previousUser?.photoUrl ||
    previousUser?.photo_url ||
    DEFAULT_AVATAR,
  role_id: updatedUser?.role_id ?? previousUser?.role_id,
});

const ProfileInfoCard = ({ icon: Icon, label, value }) => {
  if (!value) return null;

  return (
    <div className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
          <Icon />
        </div>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>

          <p className="mt-1 break-words text-sm font-bold leading-6 text-slate-800 dark:text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
};

const ModalShell = ({ title, eyebrow, children, onClose }) => (
  <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:items-center sm:p-5">
    <button
      type="button"
      className="absolute inset-0"
      onClick={onClose}
      aria-label="Fermer la modale"
    />

    <div className="relative max-h-[92dvh] w-full max-w-2xl overflow-hidden rounded-t-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950 sm:rounded-[2rem]">
      <div className="relative overflow-hidden border-b border-slate-200 bg-slate-950 p-5 text-white dark:border-white/10 sm:p-6">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.28),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.25),transparent_32%)]" />

        <div className="relative flex items-start justify-between gap-4">
          <div>
            {eyebrow && (
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-200">
                {eyebrow}
              </p>
            )}

            <h2 className="mt-2 font-titre text-2xl font-bold leading-tight sm:text-3xl">
              {title}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-950 shadow-lg transition hover:scale-105 active:scale-95"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      <div className="max-h-[calc(92dvh-112px)] overflow-y-auto p-5 sm:p-6">
        {children}
      </div>
    </div>
  </div>
);

const EditProfileModal = ({ form, onChange, onSubmit, onClose, saving, t }) => (
  <ModalShell
    title={t("profile.editProfile", { defaultValue: "Modifier mon profil" })}
    eyebrow="Informations personnelles"
    onClose={onClose}
  >
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <label
          htmlFor="prenom"
          className="mb-2 block text-sm font-bold text-slate-700 dark:text-white/80"
        >
          {t("profile.firstName", { defaultValue: "Prénom" })}
        </label>

        <input
          type="text"
          id="prenom"
          name="prenom"
          value={form.prenom}
          onChange={onChange}
          className="min-h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:focus:bg-white/[0.08]"
        />
      </div>

      <div>
        <label
          htmlFor="nom"
          className="mb-2 block text-sm font-bold text-slate-700 dark:text-white/80"
        >
          {t("profile.lastName", { defaultValue: "Nom" })}
        </label>

        <input
          type="text"
          id="nom"
          name="nom"
          value={form.nom}
          onChange={onChange}
          className="min-h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:focus:bg-white/[0.08]"
        />
      </div>

      <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 dark:border-white/10 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/10"
        >
          <FaTimes />
          {t("profile.cancel", { defaultValue: "Annuler" })}
        </button>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FaSave />
          {saving
            ? "Enregistrement..."
            : t("profile.save", { defaultValue: "Enregistrer" })}
        </button>
      </div>
    </form>
  </ModalShell>
);

const AvatarModal = ({
  currentPhoto,
  selectedAvatar,
  photoUrl,
  photoUrlError,
  onPhotoUrlChange,
  onAvatarSelection,
  onPhotoUrlSubmit,
  onClose,
  saving,
  t,
}) => {
  const previewUrl = isValidHttpUrl(photoUrl) ? photoUrl.trim() : currentPhoto;

  return (
    <ModalShell
      title={t("profile.selectAvatar", { defaultValue: "Changer ma photo" })}
      eyebrow="Avatar ou photo en ligne"
      onClose={onClose}
    >
      <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            Aperçu
          </p>

          <div className="mt-4 flex justify-center">
            <div className="relative h-36 w-36 overflow-hidden rounded-[2rem] border-4 border-white bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
              <img
                src={previewUrl || DEFAULT_AVATAR}
                alt="Aperçu de la photo de profil"
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_AVATAR;
                }}
              />
            </div>
          </div>

          <p className="mt-4 text-center text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
            Choisissez un avatar proposé ou collez l’URL d’une image en ligne.
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                Avatars proposés
              </h3>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-white/10 dark:text-white/50">
                {avatars.length} choix
              </span>
            </div>

            <div className="grid max-h-72 grid-cols-4 gap-3 overflow-y-auto rounded-[1.5rem] border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.03] sm:grid-cols-5">
              {avatars.map((avatar, index) => {
                const isSelected = selectedAvatar === avatar;

                return (
                  <button
                    key={avatar}
                    type="button"
                    onClick={() => onAvatarSelection(avatar)}
                    disabled={saving}
                    className={`relative overflow-hidden rounded-2xl border-2 bg-slate-50 p-1 transition hover:-translate-y-0.5 dark:bg-white/[0.05] ${
                      isSelected
                        ? "border-orange-400 shadow-lg shadow-orange-500/20"
                        : "border-transparent hover:border-sky-300"
                    }`}
                    aria-label={`Choisir avatar ${index + 1}`}
                  >
                    <img
                      src={avatar}
                      alt={`Avatar ${index + 1}`}
                      className="aspect-square w-full rounded-xl object-cover"
                    />

                    {isSelected && (
                      <span className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg">
                        <FaCheck className="text-xs" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={onPhotoUrlSubmit} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
            <label
              htmlFor="photoURL"
              className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-white/80"
            >
              <FaLink className="text-sky-600 dark:text-sky-300" />
              URL d’une photo en ligne
            </label>

            <input
              type="url"
              id="photoURL"
              name="photoURL"
              value={photoUrl}
              onChange={(event) => onPhotoUrlChange(event.target.value)}
              placeholder="https://exemple.com/photo.jpg"
              className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-white/[0.06] dark:text-white"
            />

            {photoUrlError && (
              <p className="mt-2 text-sm font-semibold text-red-600 dark:text-red-300">
                {photoUrlError}
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950"
            >
              <FaImage />
              {saving ? "Mise à jour..." : "Utiliser cette photo"}
            </button>
          </form>
        </div>
      </div>
    </ModalShell>
  );
};

function Profile() {
  const { t } = useTranslation();
  useScrollToTop();

  const { addNotification } = useNotification();
  const { user, updateUser } = useUser();

  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUrlError, setPhotoUrlError] = useState("");
  const [localUser, setLocalUser] = useState({
    nom: "",
    prenom: "",
  });

  const storedUser = useMemo(
    () => safeParseJson(localStorage.getItem("userInfo")),
    [],
  );

  const currentUser = user || storedUser || {};
  const userId = getUserId(currentUser);
  const photo = getUserPhoto(currentUser);
  const fullName = [currentUser?.prenom, currentUser?.nom]
    .filter(Boolean)
    .join(" ");
  const roleLabel = getRoleFromRoleId(currentUser?.role_id) || "Utilisateur";

  useEffect(() => {
    if (currentUser) {
      setLocalUser({
        nom: currentUser.nom || "",
        prenom: currentUser.prenom || "",
      });
    }
  }, [currentUser?.nom, currentUser?.prenom]);

  const updateUserEverywhere = (updatedUser) => {
    const normalizedUserData = normalizeUserData(updatedUser, currentUser);

    updateUser(normalizedUserData);
    localStorage.setItem("userInfo", JSON.stringify(normalizedUserData));

    return normalizedUserData;
  };

  const getTokenOrNotify = () => {
    const token = localStorage.getItem("token");

    if (!token) {
      addNotification("Session expirée, veuillez vous reconnecter.", "error");
      return null;
    }

    if (!userId) {
      addNotification("Utilisateur introuvable, veuillez vous reconnecter.", "error");
      return null;
    }

    return token;
  };

  const handleCloseModals = () => {
    setIsEditing(false);
    setIsUploading(false);
    setSaving(false);
    setPhotoUrlError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setLocalUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const token = getTokenOrNotify();
    if (!token) return;

    setSaving(true);

    try {
      const updatedUser = await updateUserProfile(userId, localUser, token);
      updateUserEverywhere(updatedUser);
      setIsEditing(false);
      addNotification("Profil mis à jour avec succès !", "success");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil :", error);
      addNotification("Erreur lors de la mise à jour du profil.", "error");
    } finally {
      setSaving(false);
    }
  };

  const updatePhoto = async (avatarUrl) => {
    const token = getTokenOrNotify();
    if (!token) return;

    setSaving(true);

    try {
      const updatedUser = await updateUserProfile(
        userId,
        { photoURL: avatarUrl },
        token,
      );

      updateUserEverywhere({
        ...updatedUser,
        photoURL: avatarUrl,
      });

      addNotification("Photo de profil mise à jour avec succès !", "success");
      setIsUploading(false);
      setSelectedAvatar(avatarUrl);
      setPhotoUrl("");
      setPhotoUrlError("");
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de la photo de profil :",
        error,
      );
      addNotification(
        "Erreur lors de la mise à jour de la photo de profil.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSelection = (avatarUrl) => {
    setSelectedAvatar(avatarUrl);
    updatePhoto(avatarUrl);
  };

  const handlePhotoUrlChange = (value) => {
    setPhotoUrl(value);
    setPhotoUrlError("");
  };

  const handlePhotoUrlSubmit = (event) => {
    event.preventDefault();

    const cleanUrl = String(photoUrl || "").trim();

    if (!cleanUrl) {
      setPhotoUrlError("Veuillez renseigner une URL d’image.");
      return;
    }

    if (!isValidHttpUrl(cleanUrl)) {
      setPhotoUrlError("L’URL doit commencer par http:// ou https://.");
      return;
    }

    updatePhoto(cleanUrl);
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 px-4 pb-16 pt-28 font-satoshi dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.14),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.16),transparent_32%)]" />
      <div className="absolute inset-x-0 top-0 h-72 bg-slate-950" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/80 to-transparent" />

      <section className="relative mx-auto max-w-6xl">
        <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-200 shadow-sm backdrop-blur-xl">
              <FaRegUserCircle />
              Espace personnel
            </div>

            <h1 className="mt-4 font-titre text-4xl font-bold leading-tight text-white sm:text-5xl">
              {t("profile.title", { defaultValue: "Mon profil" })}
            </h1>

            <p className="mt-3 max-w-2xl text-base font-medium leading-8 text-white/60 sm:text-lg">
              Gérez vos informations personnelles, votre avatar et l’identité
              affichée dans Judo Coach Pro.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0"
          >
            <FaPen />
            {t("profile.editInfo", { defaultValue: "Modifier mes infos" })}
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <article className="overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl shadow-slate-200/70 dark:bg-white/[0.06] dark:shadow-black/25">
            <div className="relative bg-slate-950 p-6 text-white">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.24),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.28),transparent_32%)]" />

              <div className="relative flex flex-col items-center text-center">
                <div className="relative">
                  <div className="absolute -inset-2 rounded-[2.2rem] bg-gradient-to-br from-blue-500 to-orange-400 opacity-80 blur-sm" />

                  <img
                    src={photo}
                    alt="Avatar de l'utilisateur"
                    className="relative h-36 w-36 rounded-[2rem] border-4 border-white bg-white object-cover shadow-2xl sm:h-44 sm:w-44"
                    onError={(event) => {
                      event.currentTarget.src = DEFAULT_AVATAR;
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setPhotoUrl("");
                    setPhotoUrlError("");
                    setIsUploading(true);
                  }}
                  className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm font-bold text-slate-950 shadow-lg transition hover:-translate-y-0.5 active:translate-y-0"
                >
                  <FaCamera />
                  {t("profile.changePhoto", { defaultValue: "Changer la photo" })}
                </button>

                <h2 className="mt-6 font-titre text-3xl font-bold leading-tight">
                  {fullName || "Utilisateur Judo Coach Pro"}
                </h2>

                {currentUser?.email && (
                  <p className="mt-2 text-sm font-semibold text-white/60">
                    {currentUser.email}
                  </p>
                )}
              </div>
            </div>

            <div className="p-5 sm:p-6">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  Statut du compte
                </p>

                <div className="mt-3 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                    <FaShieldAlt />
                  </div>

                  <div>
                    <p className="text-sm font-bold text-slate-950 dark:text-white">
                      Compte actif
                    </p>

                    <p className="text-xs font-medium text-slate-500 dark:text-white/50">
                      Connecté à votre espace Judo Coach Pro
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <div className="space-y-6">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-200">
                    Informations
                  </p>

                  <h2 className="mt-2 font-titre text-2xl font-bold text-slate-950 dark:text-white">
                    Identité utilisateur
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-white hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
                >
                  <FaPen className="text-xs" />
                  Modifier
                </button>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <ProfileInfoCard
                  icon={FaUser}
                  label="Prénom"
                  value={currentUser?.prenom || "Non renseigné"}
                />

                <ProfileInfoCard
                  icon={FaUser}
                  label="Nom"
                  value={currentUser?.nom || "Non renseigné"}
                />

                <ProfileInfoCard
                  icon={FaEnvelope}
                  label="Email"
                  value={currentUser?.email || "Non renseigné"}
                />

                <ProfileInfoCard
                  icon={FaIdBadge}
                  label={t("profile.chosenPlan", { defaultValue: "Rôle" })}
                  value={roleLabel}
                />
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
                  <FaLink />
                </div>

                <div>
                  <h2 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                    Photo de profil personnalisée
                  </h2>

                  <p className="mt-2 text-sm font-medium leading-7 text-slate-600 dark:text-white/55">
                    Vous pouvez choisir un avatar intégré ou utiliser l’URL d’une
                    photo hébergée en ligne. Cette image sera affichée dans votre
                    espace personnel.
                  </p>

                  {isValidHttpUrl(photo) && (
                    <a
                      href={photo}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-sky-700 transition hover:text-orange-600 dark:text-sky-200"
                    >
                      Ouvrir la photo actuelle
                      <FaExternalLinkAlt className="text-[10px]" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isEditing && (
        <EditProfileModal
          form={localUser}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onClose={handleCloseModals}
          saving={saving}
          t={t}
        />
      )}

      {isUploading && (
        <AvatarModal
          currentPhoto={photo}
          selectedAvatar={selectedAvatar || photo}
          photoUrl={photoUrl}
          photoUrlError={photoUrlError}
          onPhotoUrlChange={handlePhotoUrlChange}
          onAvatarSelection={handleAvatarSelection}
          onPhotoUrlSubmit={handlePhotoUrlSubmit}
          onClose={handleCloseModals}
          saving={saving}
          t={t}
        />
      )}
    </main>
  );
}

export default Profile;
